import React from 'react';
import { Trophy, Users, CheckCircle, Clock, Play, Crown } from 'lucide-react';
import './BracketTournament.css';

const BracketVisualization = ({ tournament, user, onSelectWinner, t }) => {
  const { bracketData } = tournament;
  const { matches, teams, totalRounds, currentRound, bracketStatus } = bracketData;

  const isAdmin = ['main_admin', 'manager', 'supervisor', 'approver'].includes(user?.role);

  // Get team info by team number
  const getTeamInfo = (teamNumber) => {
    return teams.find(t => t.teamNumber === teamNumber);
  };

  // Get matches for a specific round
  const getMatchesByRound = (round) => {
    return matches
      .filter(m => m.round === round)
      .sort((a, b) => a.matchNumber - b.matchNumber);
  };

  // Get round name
  const getRoundName = (round) => {
    if (round === totalRounds) return t('final', 'Final');
    if (round === totalRounds - 1) return t('semiFinals', 'Semi-Finals');
    if (round === totalRounds - 2) return t('quarterFinals', 'Quarter-Finals');
    return `${t('round', 'Round')} ${round}`;
  };

  // Render a single match card
  const renderMatch = (match) => {
    const team1Info = match.team1.teamNumber ? getTeamInfo(match.team1.teamNumber) : null;
    const team2Info = match.team2.teamNumber ? getTeamInfo(match.team2.teamNumber) : null;
    const isCompleted = match.status === 'completed';
    const isReady = match.status === 'ready';
    const isPending = match.status === 'pending';
    const isFinal = match.round === totalRounds;

    return (
      <div
        key={match.matchId}
        className={`bracket-match ${isCompleted ? 'completed' : ''} ${isReady ? 'ready' : ''} ${isFinal ? 'final' : ''}`}
      >
        <div className="bracket-match-header">
          <span className="match-id">{match.matchId}</span>
          <span className={`match-status status-${match.status}`}>
            {isCompleted && <CheckCircle className="w-3 h-3" />}
            {isReady && <Play className="w-3 h-3" />}
            {isPending && <Clock className="w-3 h-3" />}
            {match.status.toUpperCase()}
          </span>
        </div>

        {/* Team 1 */}
        <div
          className={`bracket-match-team ${match.winner?.teamNumber === match.team1.teamNumber ? 'winner' : ''} ${!match.team1.teamNumber ? 'tbd' : ''}`}
          onClick={() => isAdmin && isReady && match.team1.teamNumber && onSelectWinner && onSelectWinner(match.matchId, match.team1.teamNumber)}
        >
          <div className="team-info">
            {match.winner?.teamNumber === match.team1.teamNumber && <Crown className="w-4 h-4 text-yellow-400" />}
            <span className="team-name">
              {match.team1.teamNumber
                ? (match.team1.teamName || `Team ${match.team1.teamNumber}`)
                : t('tbd', 'TBD')}
            </span>
          </div>
          {team1Info && (
            <span className="team-players">{team1Info.players.length}/5</span>
          )}
        </div>

        <div className="bracket-match-vs">VS</div>

        {/* Team 2 */}
        <div
          className={`bracket-match-team ${match.winner?.teamNumber === match.team2.teamNumber ? 'winner' : ''} ${!match.team2.teamNumber ? 'tbd' : ''}`}
          onClick={() => isAdmin && isReady && match.team2.teamNumber && onSelectWinner && onSelectWinner(match.matchId, match.team2.teamNumber)}
        >
          <div className="team-info">
            {match.winner?.teamNumber === match.team2.teamNumber && <Crown className="w-4 h-4 text-yellow-400" />}
            <span className="team-name">
              {match.team2.teamNumber
                ? (match.team2.teamName || `Team ${match.team2.teamNumber}`)
                : t('tbd', 'TBD')}
            </span>
          </div>
          {team2Info && (
            <span className="team-players">{team2Info.players.length}/5</span>
          )}
        </div>

        {/* Admin select winner hint */}
        {isAdmin && isReady && !isCompleted && (
          <div className="match-admin-hint">
            {t('clickToSelectWinner', 'Click team to select winner')}
          </div>
        )}
      </div>
    );
  };

  // Render the bracket tree
  const renderBracket = () => {
    const rounds = [];

    for (let round = 1; round <= totalRounds; round++) {
      const roundMatches = getMatchesByRound(round);

      rounds.push(
        <div key={round} className={`bracket-round round-${round}`}>
          <div className="bracket-round-header">
            <span className="round-name">{getRoundName(round)}</span>
            <span className="round-matches">{roundMatches.length} {t('matches', 'matches')}</span>
          </div>
          <div className="bracket-round-matches">
            {roundMatches.map(match => renderMatch(match))}
          </div>
        </div>
      );
    }

    return rounds;
  };

  return (
    <div className="bracket-visualization">
      {/* Header */}
      <div className="bracket-viz-header">
        <div className="bracket-info">
          <Trophy className="w-5 h-5 text-yellow-400" />
          <span>{tournament.gameMode} - {tournament.map}</span>
        </div>
        <div className="bracket-status-badge">
          <span className={`status-${bracketStatus}`}>
            {bracketStatus === 'registration' && t('registration', 'Registration Open')}
            {bracketStatus === 'in_progress' && `${t('round', 'Round')} ${currentRound}/${totalRounds}`}
            {bracketStatus === 'completed' && t('completed', 'Completed')}
          </span>
        </div>
      </div>

      {/* Prize Info */}
      <div className="bracket-prize-info">
        <div className="prize-total">
          <Trophy className="w-4 h-4" />
          <span>{t('totalPrize', 'Total Prize')}: {tournament.prizePool} {t('pts', 'pts')}</span>
        </div>
        <div className="prize-per-winner">
          <Users className="w-4 h-4" />
          <span>{t('perWinner', 'Per Winner')}: {bracketData.prizePerWinner} {t('pts', 'pts')} (5 {t('players', 'players')})</span>
        </div>
      </div>

      {/* Bracket Container */}
      <div className="bracket-container">
        <div className="bracket-tree">
          {renderBracket()}
        </div>
      </div>

      {/* Winner Display (if completed) */}
      {bracketStatus === 'completed' && bracketData.bracketWinners?.length > 0 && (
        <div className="bracket-winners">
          <div className="winners-header">
            <Crown className="w-6 h-6 text-yellow-400" />
            <h3>{t('tournamentWinners', 'Tournament Winners')}</h3>
          </div>
          <div className="winners-list">
            {bracketData.bracketWinners.map((winner, idx) => (
              <div key={idx} className="winner-badge">
                <span>{winner.inGameName || winner.username || `Player ${idx + 1}`}</span>
                <span className="winner-prize">+{bracketData.prizePerWinner} {t('pts', 'pts')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketVisualization;
